import { useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Bold, Italic, List, ListOrdered, Link2 } from "lucide-react";

/* Small WYSIWYG used wherever staff write an email in their own words
   (the composer, and the Health Check invitation). Exposes insertToken() so a
   caller can drop merge fields in at the cursor. */
export const RichTextEditor = forwardRef(function RichTextEditor(
  { value, resetKey, onChange, minHeight = 240 }, ref) {
  const elRef = useRef(null);
  const savedRange = useRef(null);

  // Only overwrite the DOM when an external reset happens (template load / open),
  // never on every keystroke — that would fight the cursor.
  useEffect(() => {
    if (elRef.current && elRef.current.innerHTML !== (value || "")) {
      elRef.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && elRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const exec = (cmd, arg = null) => {
    elRef.current?.focus();
    const sel = window.getSelection();
    if (savedRange.current && sel) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
    document.execCommand(cmd, false, arg);
    onChange(elRef.current.innerHTML);
    saveSelection();
  };

  useImperativeHandle(ref, () => ({
    insertToken(token) {
      elRef.current?.focus();
      const sel = window.getSelection();
      if (savedRange.current && sel) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
      document.execCommand("insertText", false, token);
      onChange(elRef.current.innerHTML);
      saveSelection();
    },
  }));

  const Btn = ({ cmd, arg, title, children, testid }) => (
    <button type="button" title={title} data-testid={testid}
      onMouseDown={(e) => { e.preventDefault(); exec(cmd, arg); }}
      className="w-8 h-8 rounded-md flex items-center justify-center text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]">
      {children}
    </button>
  );

  return (
    <div className="rounded-[10px] border border-[#DAD5CA] bg-white overflow-hidden">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-[#EFECE4] bg-[#FBFAF7]">
        <Btn cmd="bold" title="Bold" testid="rte-bold"><Bold className="w-4 h-4" /></Btn>
        <Btn cmd="italic" title="Italic" testid="rte-italic"><Italic className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-[#E6E2D9] mx-1" />
        <Btn cmd="insertUnorderedList" title="Bulleted list" testid="rte-ul"><List className="w-4 h-4" /></Btn>
        <Btn cmd="insertOrderedList" title="Numbered list" testid="rte-ol"><ListOrdered className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-[#E6E2D9] mx-1" />
        <button type="button" title="Add link" data-testid="rte-link"
          onMouseDown={(e) => { e.preventDefault(); const url = window.prompt("Link URL", "https://"); if (url) exec("createLink", url); }}
          className="w-8 h-8 rounded-md flex items-center justify-center text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]">
          <Link2 className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={elRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onBlur={saveSelection}
        data-testid="rte-body"
        className="max-h-[420px] overflow-y-auto px-4 py-3 text-[14px] leading-relaxed text-[#1A1A1A] focus:outline-none"
        style={{ wordBreak: "break-word", minHeight }}
      />
    </div>
  );
});

export default RichTextEditor;
